import React, { useEffect, useState } from 'react';

import gql from 'graphql-tag';
import { compact } from 'lodash';
import { useMutation } from 'react-apollo';
import { Link } from 'react-router-dom';

import { GET_MAIN_APP_DATA } from 'API/MainAppData/queries';
import Form from 'Components-redesign/Form';
import { InputTypes } from 'Components-redesign/Form/fieldTypes';
import { CURRENCIES, LANGUAGES } from 'consts';
import { useFormData, useI18n } from 'Hooks';

import { getFormData_Company_venues } from '../__queries__';
import {
  updateCompanyMetadata,
  updateCompanyMetadataVariables,
  updateVenueMetadata,
  updateVenueMetadataVariables,
} from './__queries__';
import { ErrorWrapper, Wrapper } from './styles';

const NODE_QUERY = gql`
  query getNode($id: ID) {
    node: Company(id: $id) {
      id
      name
      code
      supportEmail
      active
      extended
      visibleVMCategories
      defaultOrderStatus
      membershipPointsPercentage
      serviceFee
      languages
      defaultLanguage
      currencies
      defaultCurrency
      loginMethods
      country

      hasCashback
      hasMembershipCards
      hasAppOrders
      hasPoints
      hasPOS
      hasKDS
      hasCDS
      hasTables
      hasExpress
      hasInventory
      timezone
      manualEndOfDay
      referralsEnabled

      defaultCountryCode
      useNewLicenses

      referralShareMessage {
        id
        en
        se
        es
      }

      referralTitle {
        id
        en
        se
        es
      }

      referralBody {
        id
        en
        se
        es
      }

      referralOffer {
        id
        name
      }
      referrerOffer {
        id
        name
      }
    }
  }
`;

const UPDATE_MUTATION = gql`
  mutation updateCompany($id: ID!, $data: CompanyUpdateInput!) {
    node: updateCompany(where: { id: $id }, data: $data) {
      id
      name
    }
  }
`;

const CREATE_MUTATION = gql`
  mutation createCompany($data: CompanyCreateInput!) {
    node: createCompany(data: $data) {
      id
      name
    }
  }
`;

const UPDATE_METADATA_MUTATION = gql`
  mutation updateCompanyMetadata($input: UpdateCompanyMetadataInput!) {
    updateCompanyMetadata(input: $input) {
      success
      error
      message
    }
  }
`;

const UPDATE_VENUE_METADATA_MUTATION = gql`
  mutation updateVenueMetadata($input: UpdateVenueMetadataInput!) {
    updateVenueMetadata(input: $input) {
      success
      error
      message
    }
  }
`;

export const TOGGLE_AUTO_CASH_EOD = gql`
  mutation toggleAutoCashEOD($input: ToggleAutoCashEODInput!) {
    toggleAutoCashEOD(input: $input) {
      success
    }
  }
`;

interface Props {
  offerList: any[];
  betaRoutes: string[];
  selectedID: string | undefined;
  handleSuccess: (id) => void;
  venues: getFormData_Company_venues[];
  allCompanyNames: string[];
}

const CompaniesForm: React.FC<Props> = ({
  offerList,
  betaRoutes,
  selectedID,
  handleSuccess,
  venues,
  allCompanyNames,
}) => {
  const { t } = useI18n();
  const [formValues, setFormValues] = useState<any>(null);
  const [changes, setChanges] = useState<any>(null);
  const [updateMetadata] = useMutation<updateCompanyMetadata, updateCompanyMetadataVariables>(
    UPDATE_METADATA_MUTATION,
  );
  const [updateVenueMetadata] = useMutation<updateVenueMetadata, updateVenueMetadataVariables>(
    UPDATE_VENUE_METADATA_MUTATION,
  );

  const fields: any = [
    {
      id: 'name',
      label: t('Companies.Form.Name'),
      placeHolder: t('Companies.Form.NamePlaceholder'),
      type: InputTypes.TEXT,
      mandatory: true,
      width: 50,
      validation: (val) => !allCompanyNames.includes(val.toLowerCase()),
      validationMessage: t('Companies.Form.NameValidation'),
    },
    {
      id: 'code',
      label: t('Companies.Form.Code'),
      placeHolder: t('Companies.Form.CodePlaceholder'),
      type: InputTypes.TEXT,
      maxLength: 2,
      validation: (val) => val.length === 2,
      validationMessage: t('Companies.Form.CodeValidation'),
      mandatory: true,
      width: 50,
      customMutation: (val) => val.toUpperCase(),
    },
    {
      id: 'supportEmail',
      label: t('Companies.Form.Email'),
      placeHolder: t('Companies.Form.EmailPlaceholder'),
      type: InputTypes.TEXT,
    },
    {
      id: 'hasCashback',
      label: t('Companies.Form.Cashback'),
      type: InputTypes.TOGGLE,
      value: false,
    },
    {
      id: 'hasMembershipCards',
      label: t('Companies.Form.MembershipCards'),
      type: InputTypes.TOGGLE,
      value: false,
    },
    {
      id: 'membershipPointsPercentage',
      label: t('Companies.Form.MembershipPointsPercentage'),
      placeHolder: t('Companies.Form.MembershipPointsPercentagePlaceholder'),
      type: InputTypes.INT,
      value: 0,
    },
    {
      id: 'extended',
      label: 'Extended mode',
      type: InputTypes.HIDDEN,
      value: true,
    },
    {
      id: 'hasAppOrders',
      label: t('Companies.Form.AppOrders'),
      type: InputTypes.TOGGLE,
      value: false,
    },
    {
      id: 'hasTables',
      label: t('Companies.Form.TSR'),
      type: InputTypes.TOGGLE,
      value: false,
    },
    {
      id: 'hasPOS',
      label: t('Companies.Form.POS'),
      type: InputTypes.TOGGLE,
      value: false,
    },
    {
      id: 'hasPoints',
      label: t('Companies.Form.Points'),
      type: InputTypes.TOGGLE,
      value: false,
    },
    {
      id: 'hasKDS',
      label: t('Companies.Form.KDS'),
      type: InputTypes.TOGGLE,
      value: false,
    },
    {
      id: 'hasCDS',
      label: t('Companies.Form.CDS'),
      type: InputTypes.TOGGLE,
      value: false,
    },
    {
      id: 'hasExpress',
      label: t('Companies.Form.Express'),
      type: InputTypes.TOGGLE,
      value: false,
    },
    {
      id: 'hasInventory',
      label: t('Companies.Form.Inventory'),
      type: InputTypes.TOGGLE,
      value: false,
    },
    {
      id: 'manualEndOfDay',
      label: t('Companies.Form.MEOD'),
      type: InputTypes.TOGGLE,
      value: false,
    },
    {
      id: 'active',
      label: t('Companies.Form.Active'),
      type: InputTypes.TOGGLE,
      value: true,
    },
    {
      id: 'serviceFee',
      label: t('Companies.Form.ServiceFee'),
      placeHolder: t('Companies.Form.ServiceFeePlaceHolder'),
      type: InputTypes.INT,
    },
    {
      id: 'languages',
      label: t('Companies.Form.Languages'),
      type: InputTypes.MULTISELECT,
      list: [
        { id: 'se', name: t('Companies.Form.Languages.Swedish') },
        { id: 'en', name: t('Companies.Form.Languages.English') },
        { id: 'es', name: t('Companies.Form.Languages.Spanish') },
        { id: 'no', name: t('Companies.Form.Languages.Norwegian') },
        { id: 'tr', name: t('Companies.Form.Languages.Turkish') },
        { id: 'dk', name: t('Companies.Form.Languages.Danish') },
      ],
      mandatory: true,
      customMutation: (val) => ({ set: val }),
    },
    {
      id: 'defaultLanguage',
      label: t('Companies.Form.DefaultLanguage'),
      type: InputTypes.SELECT,
      list: LANGUAGES,
      mandatory: true,
    },
    {
      id: 'timezone',
      label: 'Time zone',
      type: InputTypes.TIMEZONE,
      mandatory: true,
    },
    {
      id: 'currencies',
      label: 'Currencies',
      type: InputTypes.HIDDEN,
      value: '',
      customMutation: (value) => ({ set: [value] }),
    },
    {
      id: 'defaultCurrency',
      label: t('Companies.Form.DefaultCurrency'),
      type: InputTypes.SELECT,
      list: CURRENCIES,
      mandatory: true,
    },
    {
      id: 'loginMethods',
      label: t('Companies.Form.LoginMethods'),
      type: InputTypes.MULTISELECT,
      list: [
        { id: 'EMAIL', name: 'Email' },
        { id: 'PHONE', name: t('General.Phone') },
        { id: 'BANK', name: 'Bank' },
      ],
      customMutation: (val) => ({ set: val }),
    },
    {
      id: 'defaultOrderStatus',
      label: t('Companies.Form.DefaultOrderStatus'),
      type: InputTypes.HIDDEN,
      value: 'COMPLETED',
    },
    {
      id: 'defaultCountryCode',
      type: InputTypes.HIDDEN,
      value: '+46',
    },
    {
      id: 'legacySegmentation',
      type: InputTypes.HIDDEN,
      value: false,
    },
  ];

  if (selectedID) {
    // {new-country-onboarding} Add new countries here
    fields.push(
      {
        id: 'country',
        label: 'Country',
        type: InputTypes.SELECT,
        list: [
          { id: 'SE', name: 'Sweden' },
          { id: 'GB', name: 'United Kingdom' },
          { id: 'NO', name: 'Norway' },
          { id: 'ES', name: 'Spain' },
          { id: 'DK', name: 'Denmark' },
          { id: 'TR', name: 'Turkey' },
          // { id: 'AE', name: 'United Arab Emirates' },
        ],
        mandatory: true,
        customMutation: () => {
          // Return because it is used in triggerOtherMutation instead
          return;
        },
        triggerOtherMutation: async (val: string) => {
          await updateMetadata({
            variables: {
              input: {
                id: selectedID,
                country: val,
              },
            },
          });
        },
      },
      {
        id: 'hasIndividualEOD',
        label: 'Enable individual EOD',
        placeHolder: 'Choose venues',
        type: InputTypes.MULTISELECT,
        ignoreInMutation: true,
        ignoreServerData: true,
        value: venues.filter((venue) => venue.hasIndividualEOD).map((venue) => venue.id) || [],
        list: venues.map((venue) => ({
          id: venue.id,
          name: venue.name,
        })),
        customMutation: () => {
          // Return because it is used in triggerOtherMutation instead
          return;
        },
        triggerOtherMutation: async (values: string[]) => {
          for (const venue of venues) {
            await updateVenueMetadata({
              variables: {
                input: {
                  venueId: venue.id,
                  hasIndividualEOD: values.includes(venue.id),
                },
              },
            });
          }
        },
      },
      ...[
        {
          id: 'titlebar',
          type: InputTypes.TITLEBAR,
          placeHolder: 'Referrals',
        },
        {
          id: 'referralsEnabled',
          label: 'Referrals enabled',
          type: InputTypes.TOGGLE,
          value: false,
        },
        {
          id: 'referralTitle',
          label: 'Referral Page Title',
          langPlaceHolder: {
            se: 'Enter referral page title',
            en: 'Enter referral page title',
            es: 'Enter referral page title',
          },
          type: InputTypes.TEXTLANG,
        },
        {
          id: 'referralTitle',
          label: 'Referral Page Body',
          langPlaceHolder: {
            se: 'Enter referral page body',
            en: 'Enter referral page body',
            es: 'Enter referral page body',
          },
          type: InputTypes.TEXTLANG,
        },
        {
          id: 'referralShareMessage',
          label: 'Referral Share Message',
          langPlaceHolder: {
            se: 'Enter referral share message',
            en: 'Enter referral share message',
            es: 'Enter referral share message',
          },
          type: InputTypes.TEXTLANG,
        },
        {
          id: 'referralOffer',
          label: 'Referral Offer',
          type: InputTypes.SELECT,
          list: offerList ?? [],
          customMutation: (val, prevVal) => {
            if (!prevVal && !val) {
              return;
            } else if (prevVal && !val.length) {
              return { disconnect: true };
            } else {
              return { connect: { id: val.id ?? val } };
            }
          },
        },
        {
          id: 'referrerOffer',
          label: 'Referrer Offer',
          type: InputTypes.SELECT,
          list: offerList ?? [],
          customMutation: (val, prevVal) => {
            if (!prevVal && !val) {
              return;
            } else if (prevVal && !val.length) {
              return { disconnect: true };
            } else {
              return { connect: { id: val.id ?? val } };
            }
          },
        },
        {
          id: 'titlebar_beta',
          type: InputTypes.TITLEBAR,
          placeHolder: t('Companies.Form.BETA'),
        },
        {
          id: 'visibleVMCategories',
          label: t('Companies.Form.BETARoutes'),
          type: InputTypes.MULTISELECT,
          list: betaRoutes,
          customMutation: (val) => ({ set: val }),
        },
        {
          id: 'useNewLicenses',
          label: 'Use new licenses (check /licenses)',
          type: InputTypes.TOGGLE,
          ignoreInMutation: true,
          value: false,
          triggerOtherMutation: async (val: boolean) => {
            await updateMetadata({
              variables: {
                input: {
                  id: selectedID,
                  useNewLicenses: val,
                },
              },
            });
          },
        },
      ],
    );
  }

  const fieldsToShow = compact(fields);

  const [create] = useMutation<any>(CREATE_MUTATION, {
    refetchQueries: [
      { query: GET_MAIN_APP_DATA }, // DocumentNode object parsed with gql
    ],
  });
  const [update] = useMutation<any>(UPDATE_MUTATION, {
    refetchQueries: [
      { query: GET_MAIN_APP_DATA }, // DocumentNode object parsed with gql
    ],
  });
  const [formState, getMutationData] = useFormData(selectedID, fieldsToShow, NODE_QUERY);

  const currency = changes && changes.defaultCurrency?.value;

  useEffect(() => {
    if (currency && currency.length > 0) {
      const newChanges = changes;
      newChanges.currencies.value = currency;
      setFormValues(newChanges);
    }
  }, [changes?.defaultCurrency]);

  const defaultLanguage = changes && changes.defaultLanguage?.value;
  const languages = changes && changes.languages?.value;

  useEffect(() => {
    if (defaultLanguage && defaultLanguage.length > 0 && languages && languages.length > 0) {
      const newChanges = changes;

      const languagesObj = changes && changes.languages;
      const languagesValues = languagesObj?.value;

      if (languagesValues.includes(defaultLanguage) && languagesObj.validationMessage) {
        const { validation, validationMessage, ...withoutValidation } = languagesObj;
        newChanges.languages = withoutValidation;
      } else if (!languagesValues.includes(defaultLanguage) && !languagesObj.validationMessage) {
        const newLanguagesValue = newChanges.languages;
        newLanguagesValue.validation = () => false;
        newLanguagesValue.validationMessage = t('Companies.DefaultLanguageMustBeIncluded');
      }

      if (newChanges.languages.value.includes('da')) {
        newChanges.languages.value = newChanges.languages.value.filter((change) => change !== 'da');
      }

      setFormValues(newChanges);
    }
  }, [changes?.defaultLanguage, changes?.languages]);

  return (
    <Wrapper>
      {!selectedID && (
        <ErrorWrapper>
          Do not use this form to create a company, use{' '}
          <Link to="/create-company">
            this page instead. Or use quick office. Using this form will cause issues and bugs.
          </Link>
        </ErrorWrapper>
      )}
      <Form
        fields={formState}
        idToUpdate={selectedID}
        floatingButton
        updateMutation={update}
        createMutation={create}
        parseMutationVariables={getMutationData}
        noCompanyConnect
        onFormSuccess={handleSuccess}
        formValues={formValues}
        getFieldChanges={(changes) => setChanges(changes)}
        venues={venues}
        isCompanyForm
      />
    </Wrapper>
  );
};

export default CompaniesForm;
